import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Send, Loader2, MessageSquare, CalendarDays, MapPin, ExternalLink,
} from "lucide-react";
import MessageAttachmentPicker from "@/components/MessageAttachmentPicker";
import MessageAttachmentList from "@/components/MessageAttachmentList";

/**
 * VendorEventMessages — the vendor-side twin of `EventMessagesPage`.
 *
 * A vendor can be talking to multiple organizers across multiple events at
 * once. This page shows a flat inbox of ALL threads (grouped by event) and
 * an active chat pane on the right. Reuses the same
 * `GET /api/events/{eventId}/threads/{vendorId}` endpoint the organizer
 * side does — access is scoped so the vendor only ever sees their own.
 */
export default function VendorEventMessages() {
  const { user, loading: authLoading } = useAuth();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // { event_id, event_title }

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/vendor/event-threads");
      setThreads(data.items || []);
    } catch (e) {
      toast.error("Couldn't load your event messages");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user?.role !== "vendor") return;
    reload();
  }, [user, reload]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login?next=/vendor/event-messages" replace />;
  if (user.role !== "vendor") return <Navigate to="/" replace />;

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="vendor-event-messages-page">
      <div>
        <div className="overline" style={{ color: "var(--avt-purple)" }}>Event messages</div>
        <h1 className="font-serif text-4xl mt-3">Your event inbox.</h1>
        <p className="mt-1 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
          Every conversation with an organizer is grouped by event so you never
          lose the thread. Click one to read + reply.
        </p>
      </div>

      <div className="mt-8 grid md:grid-cols-[320px_1fr] gap-5">
        <aside data-testid="vendor-threads-sidebar">
          {loading ? (
            <div className="text-center py-10">
              <Loader2 className="h-6 w-6 mx-auto animate-spin" style={{ color: "var(--avt-purple)" }} />
            </div>
          ) : threads.length === 0 ? (
            <div className="card-warm rounded-2xl p-6 text-center"
                 data-testid="vendor-threads-empty">
              <MessageSquare className="h-8 w-8 mx-auto" style={{ color: "var(--avt-purple)" }} />
              <p className="mt-3 text-sm italic" style={{ color: "var(--ink-soft)" }}>
                Nothing here yet. When an organizer messages you about an event,
                the conversation will show up here.
              </p>
            </div>
          ) : (
            <ul className="space-y-1">
              {threads.map((t) => (
                <li key={t.event_id}>
                  <button
                    onClick={() => setActive({ event_id: t.event_id, event_title: t.event_title })}
                    data-testid={`vendor-thread-${t.event_id}`}
                    className="w-full text-left px-3 py-3 rounded-md transition-colors border"
                    style={{
                      backgroundColor: active?.event_id === t.event_id ? "var(--sand)" : "var(--paper-warm)",
                      borderColor: "var(--border-warm)",
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{t.event_title}</div>
                        <div className="text-xs mt-0.5 flex flex-wrap gap-2"
                             style={{ color: "var(--ink-soft)" }}>
                          {t.event_date && (
                            <span><CalendarDays className="h-3 w-3 inline mr-1" />{t.event_date}</span>
                          )}
                          {t.event_city && (
                            <span><MapPin className="h-3 w-3 inline mr-1" />{t.event_city}</span>
                          )}
                        </div>
                        <div className="text-xs mt-1.5 truncate" style={{ color: "var(--ink)" }}>
                          <span className="font-semibold text-[10px] uppercase tracking-widest mr-1"
                                style={{ color: "var(--ink-soft)" }}>
                            {t.last_sender_role === "vendor" ? "You:" : "Organizer:"}
                          </span>
                          {t.last_message}
                        </div>
                      </div>
                      {t.unread > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: "var(--avt-pink)", color: "#fff" }}
                              data-testid={`vendor-thread-unread-${t.event_id}`}>
                          {t.unread}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section data-testid="vendor-threads-main">
          {active ? (
            <VendorThreadPane
              eventId={active.event_id}
              eventTitle={active.event_title}
              vendorId={user.id}
              onSent={reload}
            />
          ) : (
            <div className="card-warm rounded-2xl p-10 text-center">
              <MessageSquare className="h-10 w-10 mx-auto" style={{ color: "var(--avt-purple)" }} />
              <h2 className="mt-4 font-serif text-2xl">Pick an event to open the thread.</h2>
              <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
                Threads are on the left. Unread messages have a pink dot.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function VendorThreadPane({ eventId, eventTitle, vendorId, onSent }) {
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/threads/${vendorId}`);
      setThread(data);
    } catch (e) {
      setThread({ messages: [], vendor: {}, event: { title: eventTitle } });
    } finally { setLoading(false); }
  }, [eventId, vendorId, eventTitle]);

  useEffect(() => { setLoading(true); setAttachments([]); load(); }, [load]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread?.messages?.length]);

  const canSend = !!(body.trim() || attachments.length > 0);
  const send = async () => {
    if (sending || !canSend) return;
    setSending(true);
    try {
      await api.post(`/events/${eventId}/threads/${vendorId}`,
        { body: body.trim(), attachments });
      setBody("");
      setAttachments([]);
      await load();
      onSent?.();
    } catch (e) {
      toast.error("Couldn't send message");
    } finally { setSending(false); }
  };

  if (loading) return (
    <div className="card-warm rounded-2xl p-10 text-center">
      <Loader2 className="h-6 w-6 mx-auto animate-spin" style={{ color: "var(--avt-purple)" }} />
    </div>
  );

  return (
    <div className="card-warm rounded-2xl flex flex-col" style={{ minHeight: 520 }}
         data-testid="vendor-thread-pane">
      <div className="px-5 py-4 border-b"
           style={{ borderColor: "var(--border-warm)" }}>
        <div className="text-sm font-semibold" data-testid="vendor-thread-event-title">
          {thread?.event?.title || eventTitle}
        </div>
        {(thread?.event?.date || thread?.event?.city) && (
          <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>
            {thread.event.date}{thread.event.city ? ` · ${thread.event.city}` : ""}
          </div>
        )}
        {eventId && (
          <Link to={`/events/${eventId}`} target="_blank"
                className="text-xs underline"
                style={{ color: "var(--ink-soft)" }}
                data-testid="vendor-thread-view-event">
            View event <ExternalLink className="h-3 w-3 inline" />
          </Link>
        )}
      </div>
      <div className="flex-1 px-5 py-4 space-y-2 overflow-y-auto"
           style={{ maxHeight: 460 }} data-testid="vendor-thread-messages">
        {thread.messages.length === 0 ? (
          <p className="text-sm italic text-center py-8" style={{ color: "var(--ink-soft)" }}>
            No messages yet.
          </p>
        ) : thread.messages.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
        <div ref={endRef} />
      </div>
      <div className="border-t px-5 py-3"
           style={{ borderColor: "var(--border-warm)" }}>
        <div className="flex items-center gap-1 flex-wrap">
          <MessageAttachmentPicker
            attachments={attachments}
            onChange={setAttachments}
            disabled={sending}
            testIdPrefix="vendor-msg-attach"
          />
          <Input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type your reply…"
            disabled={sending}
            className="flex-1"
            data-testid="vendor-thread-input"
          />
          <Button onClick={send} disabled={sending || !canSend}
                  className="btn-primary rounded-full"
                  data-testid="vendor-thread-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" />
                     : <><Send className="h-4 w-4" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Bubble({ m }) {
  // From the vendor's POV, "mine" is when THEY sent it — role === "vendor".
  const mine = m.sender_role === "vendor";
  const hasAttachments = !!(m.attachments && m.attachments.length);
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
         data-testid={`vendor-bubble-${m.sender_role}`}>
      {m.body && (
        <div
          className="max-w-[75%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap"
          style={{
            backgroundColor: mine ? "var(--avt-purple)" : "var(--sand)",
            color: mine ? "#fff" : "var(--ink)",
          }}
        >
          {m.body}
        </div>
      )}
      {hasAttachments && (
        <MessageAttachmentList attachments={m.attachments} mine={mine} />
      )}
    </div>
  );
}
